// Three states, and the third one is not "empty".
//
// A screen reading live data has to render loading, failed, and ready, and the
// mistake this hook exists to prevent is folding a fourth state — "ready, with
// nothing in it" — into one of the first three. An account with no usage is
// READY: the ledger answered, and what it said is that there is nothing yet.
// Rendering that as a spinner would hang forever on a correct answer, and
// rendering it as a failure would tell a customer their portal is broken on
// their first day. So `ready` carries the data whether or not the data is
// empty, and each page decides what an empty version of itself looks like.
//
// One read per mount, no polling, no cache. A metering page that refetched on a
// timer would be a page that moved under somebody reading a row, and the ledger
// this reads is written hourly.

import { useEffect, useState } from 'react';

import { PortalRequestFailed, type AuthorizedFetch } from '../auth/api';
import { usePortalSession } from '../auth/context';

export type PortalRead<T> =
  | { status: 'loading' }
  | { status: 'failed'; message: string }
  | { status: 'ready'; data: T };

/**
 * Run one authorized read and report where it got to.
 *
 * @param read A module-level function taking the session's `authorizedFetch`.
 *   It goes in the effect's dependency array, so it must be stable across
 *   renders — an inline arrow would re-run the request on every render and
 *   never settle. The reads in `data/reads.ts` are module-level for this
 *   reason.
 */
export function usePortalRead<T>(
  read: (authorizedFetch: AuthorizedFetch) => Promise<T>,
): PortalRead<T> {
  const { authorizedFetch } = usePortalSession();
  const [state, setState] = useState<PortalRead<T>>({ status: 'loading' });

  useEffect(() => {
    let current = true;

    read(authorizedFetch)
      .then((data) => {
        if (current) {
          setState({ status: 'ready', data });
        }
      })
      .catch((failure: unknown) => {
        if (!current) {
          return;
        }
        // A refusal is the service's own wording and is shown as-is. Anything
        // else — a network drop, a proxy answering instead of the service —
        // has no wording to borrow, so it gets one that does not claim to know
        // what went wrong.
        setState({
          status: 'failed',
          message:
            failure instanceof PortalRequestFailed
              ? failure.message
              : 'This screen could not be loaded. Check your connection and try again.',
        });
      });

    return () => {
      current = false;
    };
  }, [read, authorizedFetch]);

  return state;
}
